import React, { useState, useEffect } from 'react';
import CertificationList from './components/CertificationList';
import CreateCertification from './components/CreateCertification';
import LearnerProgress from './components/LearnerProgress';
import ExpiryRenewals from './components/ExpiryRenewals';
import useCertificationManagementStore from '@/stores/AdminStore/CertificationManagementStore';
import './CertificationManagement.css';

type TabType = 'list' | 'create' | 'progress' | 'expiry';

interface TabItem {
  id: TabType;
  label: string;
  badge?: number;
}

const CertificationManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('list');

  const {
    certifications,
    learnerProgress,
    certificationStats,
    fetchCertifications,
    fetchLearnerProgress,
    fetchCertificationStats,
  } = useCertificationManagementStore();

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'list' && !certifications) {
      fetchCertifications();
    }
    if (activeTab === 'list' && !certificationStats) {
      fetchCertificationStats();
    }
  }, [activeTab, certifications, certificationStats, fetchCertifications, fetchCertificationStats]);

  useEffect(() => {
    if (activeTab === 'progress' && !learnerProgress) {
      fetchLearnerProgress();
    }
  }, [activeTab, learnerProgress, fetchLearnerProgress]);

  const tabs: TabItem[] = [
    { id: 'list', label: 'Certification List' },
    { id: 'create', label: 'Create Certification' },
    { id: 'progress', label: 'Learner Progress' },
    { id: 'expiry', label: 'Expiry & Renewals', badge: 2 },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'list':
        return <CertificationList />;
      case 'create':
        return <CreateCertification />;
      case 'progress':
        return <LearnerProgress />;
      case 'expiry':
        return <ExpiryRenewals />;
      default:
        return <CertificationList />;
    }
  };

  return (
    <div className="admin-certifi-management">
      <div className="admin-certifi-header">
        <div className="admin-certifi-breadcrumb">
          <span>Content Management</span>
          <span className="admin-certifi-separator">›</span>
          <span>Certification Management</span>
        </div>
        <h1>Certification Management</h1>
        <p>Issue, track, and manage professional certifications</p>
      </div>

      <div className="admin-certifi-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`admin-certifi-tab ${activeTab === tab.id ? 'admin-certifi-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.badge && <span className="admin-certifi-tab-badge">{tab.badge}</span>}
          </button>
        ))}
      </div>

      <div className="admin-certifi-content">{renderTabContent()}</div>
    </div>
  );
};

export default CertificationManagement;
